import type {
    IReaderView,
    IReaderViewAnnotation,
    IReaderViewAnnotationEngineEvent,
    IReaderViewAnnotationLayer,
    IReaderViewAnnotationLayerOptions,
    IReaderViewAnnotationMouseEngineEvent,
    IReaderViewAnnotationOptions,
    IReaderViewAnnotationPointerEngineEvent,
} from '@colibrio/colibrio-reader-framework/colibrio-readingsystem-base';
import { getCurrentInstance, onBeforeUnmount } from 'vue';

type AnnotationLayerEvents<TData> = {
    annotationAdded: { locator: string; annotation: IReaderViewAnnotation<TData> };
    annotationDeleted: { locator: string; annotation: IReaderViewAnnotation<TData> };
    collectionUpdated: undefined;
    annotationEnterView: { annotation: IReaderViewAnnotation<TData>; data: TData };
    annotationExitView: { annotation: IReaderViewAnnotation<TData>; data: TData };
    annotationClick: IReaderViewAnnotationMouseEngineEvent;
    annotationContextMenu: IReaderViewAnnotationMouseEngineEvent;
    annotationPointerEnter: IReaderViewAnnotationPointerEngineEvent;
    annotationPointerLeave: IReaderViewAnnotationPointerEngineEvent;
};

type AnnotationLayerEventName = keyof AnnotationLayerEvents<unknown>;
type AnnotationLayerEventHandler<TData, TEvent extends AnnotationLayerEventName> = (_payload: AnnotationLayerEvents<TData>[TEvent]) => void;

export interface IAnnotationLayerControllerOptions {
    layerOptions?: IReaderViewAnnotationLayerOptions;
    defaultAnnotationOptions?: IReaderViewAnnotationOptions;
}

export class AnnotationLayerController<TData = unknown> {
    readonly name: string;

    private readonly layer: IReaderViewAnnotationLayer;
    private readonly locatorToAnnotationMap = new Map<string, IReaderViewAnnotation<TData>>();
    private readonly annotationsInVisibleRange = new Set<TData>();
    private readonly callbacks = new Map<AnnotationLayerEventName, Set<(_payload: unknown) => void>>();

    constructor(
        private readonly readerView: IReaderView,
        name: string,
        options?: IAnnotationLayerControllerOptions,
    ) {
        this.name = name;
        this.layer = readerView.createAnnotationLayer(name);

        if (options?.layerOptions) {
            this.layer.setLayerOptions(options.layerOptions);
        }

        if (options?.defaultAnnotationOptions) {
            this.layer.setDefaultAnnotationOptions(options.defaultAnnotationOptions);
        }

        this.bindEngineEvents();
    }

    getColibrioLayer(): IReaderViewAnnotationLayer {
        return this.layer;
    }

    setLayerOptions(options: IReaderViewAnnotationLayerOptions): void {
        this.layer.setLayerOptions(options);
    }

    setDefaultAnnotationOptions(options: IReaderViewAnnotationOptions): void {
        this.layer.setDefaultAnnotationOptions(options);
    }

    setVisible(isVisible: boolean): void {
        this.layer.setVisible(isVisible);
    }

    on<TEvent extends AnnotationLayerEventName>(
        eventName: TEvent,
        callback: AnnotationLayerEventHandler<TData, TEvent>,
    ): () => void {
        const callbackSet = this.callbacks.get(eventName) ?? new Set<(_payload: unknown) => void>();
        callbackSet.add(callback as (_payload: unknown) => void);
        this.callbacks.set(eventName, callbackSet);

        return () => {
            const existingSet = this.callbacks.get(eventName);
            existingSet?.delete(callback as (_payload: unknown) => void);
        };
    }

    createAnnotation(locator: string, customData?: TData): IReaderViewAnnotation<TData> {
        const annotation = this.layer.createAnnotation<TData>(locator);
        if (customData !== undefined) {
            annotation.setCustomData(customData);
        }

        this.locatorToAnnotationMap.set(locator, annotation);
        this.emit('annotationAdded', { locator, annotation });
        this.emit('collectionUpdated', undefined);

        return annotation;
    }

    hasAnnotation(locator: string): boolean {
        return this.locatorToAnnotationMap.has(locator);
    }

    getAnnotation(locator: string): IReaderViewAnnotation<TData> | undefined {
        return this.locatorToAnnotationMap.get(locator);
    }

    getCustomData(locator: string): TData | null | undefined {
        return this.locatorToAnnotationMap.get(locator)?.getCustomData();
    }

    getAnnotations(): IReaderViewAnnotation<TData>[] {
        return this.layer.getAnnotations() as IReaderViewAnnotation<TData>[];
    }

    getAllCustomData(): TData[] {
        return this.getAnnotations()
            .map((annotation) => annotation.getCustomData())
            .filter((data): data is TData => data !== undefined);
    }

    deleteAnnotation(locator: string): void {
        const annotation = this.locatorToAnnotationMap.get(locator);
        if (!annotation) {
            return;
        }

        annotation.getAnnotationLayer().destroyAnnotation(annotation);
        this.locatorToAnnotationMap.delete(locator);

        this.emit('annotationDeleted', { locator, annotation });
        this.emit('collectionUpdated', undefined);
    }

    clear(): void {
        this.layer.destroyAllAnnotations();
        this.locatorToAnnotationMap.clear();
        this.annotationsInVisibleRange.clear();
        this.emit('collectionUpdated', undefined);
    }

    destroy(): void {
        this.clear();
        this.readerView.destroyAnnotationLayer(this.layer);
        this.callbacks.clear();
    }

    getAnnotationsInVisibleRange(): TData[] {
        return Array.from(this.annotationsInVisibleRange);
    }

    private bindEngineEvents(): void {
        this.layer.addEngineEventListener('annotationClick', this.onClick);
        this.layer.addEngineEventListener('annotationContextMenu', this.onContextMenu);
        this.layer.addEngineEventListener('annotationPointerEnter', this.onPointerEnter);
        this.layer.addEngineEventListener('annotationPointerLeave', this.onPointerLeave);
        this.layer.addEngineEventListener('annotationIntersectingVisibleRange', this.onEnterView);
        this.layer.addEngineEventListener('annotationOutsideVisibleRange', this.onExitView);
    }

    private onClick = (event: IReaderViewAnnotationMouseEngineEvent): void => {
        this.emit('annotationClick', event);
    };

    private onContextMenu = (event: IReaderViewAnnotationMouseEngineEvent): void => {
        this.emit('annotationContextMenu', event);
    };

    private onPointerEnter = (event: IReaderViewAnnotationPointerEngineEvent): void => {
        this.emit('annotationPointerEnter', event);
    };

    private onPointerLeave = (event: IReaderViewAnnotationPointerEngineEvent): void => {
        this.emit('annotationPointerLeave', event);
    };

    private onEnterView = (event: IReaderViewAnnotationEngineEvent): void => {
        const annotation = event.annotation as IReaderViewAnnotation<TData>;
        const data = annotation.getCustomData();

        if (data) {
            this.annotationsInVisibleRange.add(data);
            this.emit('annotationEnterView', { annotation, data });
        }
    };

    private onExitView = (event: IReaderViewAnnotationEngineEvent): void => {
        const annotation = event.annotation as IReaderViewAnnotation<TData>;
        const data = annotation.getCustomData();

        if (data) {
            this.annotationsInVisibleRange.delete(data);
            this.emit('annotationExitView', { annotation, data });
        }
    };

    private emit<TEvent extends AnnotationLayerEventName>(
        eventName: TEvent,
        payload: AnnotationLayerEvents<TData>[TEvent],
    ): void {
        const callbackSet = this.callbacks.get(eventName);
        if (!callbackSet) {
            return;
        }

        callbackSet.forEach((callback) => {
            callback(payload);
        });
    }
}

interface IAnnotationLayerRegistryEntry {
    refs: number;
    controller: AnnotationLayerController<unknown>;
}

const annotationLayerRegistry = new Map<string, IAnnotationLayerRegistryEntry>();

export interface IUseAnnotationLayerOptions extends IAnnotationLayerControllerOptions {
    key?: string;
    destroyOnLastUnmount?: boolean;
}

export const useAnnotationLayer = <TData = unknown>(
    layerName: string,
    options?: IUseAnnotationLayerOptions,
): AnnotationLayerController<TData> => {
    const key = options?.key ?? layerName;
    const existing = annotationLayerRegistry.get(key);

    if (existing) {
        existing.refs += 1;
        registerAutoRelease(key, options?.destroyOnLastUnmount ?? false);
        return existing.controller as AnnotationLayerController<TData>;
    }

    const { $cirrusReaderView } = useNuxtApp();
    const controller = new AnnotationLayerController<TData>(
        $cirrusReaderView.colibrioReaderView,
        layerName,
        {
            layerOptions: options?.layerOptions,
            defaultAnnotationOptions: options?.defaultAnnotationOptions,
        },
    );

    annotationLayerRegistry.set(key, {
        refs: 1,
        controller: controller as AnnotationLayerController<unknown>,
    });

    registerAutoRelease(key, options?.destroyOnLastUnmount ?? false);

    return controller;
};

const registerAutoRelease = (key: string, destroyOnLastUnmount: boolean): void => {
    if (!destroyOnLastUnmount || !getCurrentInstance()) {
        return;
    }

    onBeforeUnmount(() => {
        releaseAnnotationLayer(key);
    });
};

export const releaseAnnotationLayer = (key: string): void => {
    const existing = annotationLayerRegistry.get(key);
    if (!existing) {
        return;
    }

    existing.refs -= 1;
    if (existing.refs <= 0) {
        existing.controller.destroy();
        annotationLayerRegistry.delete(key);
    }
};

export const destroyAnnotationLayer = (key: string): void => {
    const existing = annotationLayerRegistry.get(key);
    if (!existing) {
        return;
    }

    existing.controller.destroy();
    annotationLayerRegistry.delete(key);
};

export const clearAnnotationLayerRegistry = (): void => {
    Array.from(annotationLayerRegistry.keys()).forEach((key) => {
        destroyAnnotationLayer(key);
    });
};
